'use client';

import React, { useState } from 'react';
import { Plus, Calendar, X, Check, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';

interface Campaign {
  id: string;
  name: string;
  discount: number;
  startDate: string;
  endDate: string;
  products: string[];
  status: 'active' | 'scheduled' | 'expired';
}

const initialCampaigns: Campaign[] = [
  { id: 'c1', name: language => language === 'ar' ? 'عرض رمضان 2026' : 'Promo Ramadan 2026', discount: 15, startDate: '2026-03-01', endDate: '2026-03-31', products: ['Fromage Frais 1kg', 'Yaourt Nature 500g', 'Lait Entier 1L'], status: 'expired' },
  { id: 'c2', name: language => language === 'ar' ? 'تخفيضات الصيف' : 'Soldes Été', discount: 10, startDate: '2026-06-01', endDate: '2026-08-31', products: ['Jus Orange 1L', 'Eau Minérale 1.5L'], status: 'scheduled' },
  { id: 'c3', name: language => language === 'ar' ? 'عرض نهاية الأسبوع' : 'Promo Week-end', discount: 8, startDate: '2026-05-24', endDate: '2026-05-26', products: ['Fromage Frais 1kg'], status: 'active' },
].map(c => ({ ...c, name: c.name as unknown as string }));

const campaignsData: Campaign[] = [
  { id: 'c1', name: 'Promo Ramadan 2026', discount: 15, startDate: '2026-03-01', endDate: '2026-03-31', products: ['Fromage Frais 1kg', 'Yaourt Nature 500g', 'Lait Entier 1L'], status: 'expired' },
  { id: 'c2', name: 'Soldes Été', discount: 10, startDate: '2026-06-01', endDate: '2026-08-31', products: ['Jus Orange 1L', 'Eau Minérale 1.5L'], status: 'scheduled' },
  { id: 'c3', name: 'Promo Week-end', discount: 8, startDate: '2026-05-24', endDate: '2026-05-26', products: ['Fromage Frais 1kg'], status: 'active' },
  { id: 'c4', name: 'Fidélité Clients VIP', discount: 12, startDate: '2026-05-01', endDate: '2026-05-31', products: ['Fromage Frais 1kg', 'Beurre 250g'], status: 'active' },
];

const statusConfig = {
  active: { ar: 'نشطة', fr: 'Active', icon: CheckCircle, cls: 'text-success bg-success/10' },
  scheduled: { ar: 'مجدولة', fr: 'Planifiée', icon: Clock, cls: 'text-info bg-info/10' },
  expired: { ar: 'منتهية', fr: 'Expirée', icon: AlertCircle, cls: 'text-muted-foreground bg-muted' },
};

export default function PromoCampaignsContent() {
  const { language, dir } = useLanguage();
  const t = getTranslations(language);
  const [campaigns, setCampaigns] = useState<Campaign[]>(campaignsData);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', discount: '', startDate: '', endDate: '' });
  const [filter, setFilter] = useState<'all' | 'active' | 'scheduled' | 'expired'>('all');

  const filtered = filter === 'all' ? campaigns : campaigns.filter(c => c.status === filter);

  const addCampaign = () => {
    if (!form.name || !form.discount || !form.startDate || !form.endDate) return;
    const now = new Date().toISOString().split('T')[0];
    const status: Campaign['status'] = form.startDate > now ? 'scheduled' : form.endDate < now ? 'expired' : 'active';
    setCampaigns([...campaigns, { id: `c${Date.now()}`, name: form.name, discount: Number(form.discount), startDate: form.startDate, endDate: form.endDate, products: [], status }]);
    setForm({ name: '', discount: '', startDate: '', endDate: '' });
    setShowForm(false);
  };

  const deleteCampaign = (id: string) => setCampaigns(campaigns.filter(c => c.id !== id));

  return (
    <div className="space-y-6 fade-in" dir={dir}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-700 text-foreground">{t('promo.title')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('promo.subtitle')}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex-shrink-0">
          <Plus size={16} /> {t('promo.add')}
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {(['all', 'active', 'scheduled', 'expired'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-colors ${filter === f ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
            {f === 'all' ? t('common.all') : f === 'active' ? t('promo.active') : f === 'scheduled' ? t('promo.scheduled') : t('promo.expired')}
            <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-2xs">
              {f === 'all' ? campaigns.length : campaigns.filter(c => c.status === f).length}
            </span>
          </button>
        ))}
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-card space-y-4">
          <h3 className="font-600 text-foreground">{t('promo.add')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className="input-base" placeholder={t('promo.campaign')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <input className="input-base" type="number" placeholder={`${t('promo.discount')} (%)`} value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })} />
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('promo.start')}</label>
              <input className="input-base" type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('promo.end')}</label>
              <input className="input-base" type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addCampaign} className="btn-primary"><Check size={16} /> {t('common.save')}</button>
            <button onClick={() => setShowForm(false)} className="btn-secondary"><X size={16} /> {t('common.cancel')}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((c) => {
          const cfg = statusConfig[c.status];
          const StatusIcon = cfg.icon;
          return (
            <div key={c.id} className="bg-card border border-border rounded-xl p-5 shadow-card card-hover">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-700 text-foreground truncate">{c.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center gap-1 text-2xs font-600 px-2 py-0.5 rounded-full ${cfg.cls}`}>
                      <StatusIcon size={10} />
                      {cfg[language]}
                    </span>
                    <span className="text-2xs font-700 text-accent bg-accent/10 px-2 py-0.5 rounded-full">-{c.discount}%</span>
                  </div>
                </div>
                <button onClick={() => deleteCampaign(c.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-danger/10 transition-colors flex-shrink-0">
                  <X size={14} className="text-danger" />
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                <Calendar size={12} />
                <span>{c.startDate} → {c.endDate}</span>
              </div>
              {c.products.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {c.products.map(p => (
                    <span key={p} className="text-2xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-500">{p}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
